// PMax Search Themes — Audience Signals section, deterministic check.
// Per-asset-group search-theme count + a sample of the themes, fewest first.

import classNames from 'classnames';

import type { EvidenceProps } from './registry';

interface AssetGroupRow {
  id?: string;
  name: string;
  campaignName: string;
  searchThemeCount: number;
  searchThemeSample: string[];
  themesTruncated: number;
}

interface SearchThemesShape {
  totalAssetGroups: number;
  noThemeCount: number;
  thinThemeCount: number;
  thinThemeFloor: number;
  assetGroups: AssetGroupRow[];
}

export const PmaxSearchThemesEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as SearchThemesShape;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Search theme coverage
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalAssetGroups} asset groups
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.totalAssetGroups.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Asset groups</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.noThemeCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>No themes</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.thinThemeCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Under {e.thinThemeFloor}</div>
          </div>
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Per-asset-group search themes
          <span className='evidence__section-tag evidence__section-tag--context'>fewest first</span>
        </h4>
        <table className='evidence__table evidence__table--pmax-themes'>
          <thead>
            <tr>
              <th>Asset group</th>
              <th className='evidence__th--right'>Themes</th>
              <th>Sample</th>
            </tr>
          </thead>
          <tbody>
            {e.assetGroups.map(g => (
              <tr
                key={g.id ?? g.name}
                className={classNames({ 'evidence__row--flagged': g.searchThemeCount === 0 })}
              >
                <td>
                  <div>{g.name}</div>
                  <div className='evidence__action-id'>{g.campaignName}</div>
                </td>
                <td className='evidence__td--right'>{g.searchThemeCount.toLocaleString()}</td>
                <td>
                  {g.searchThemeCount === 0 ? (
                    <span className='evidence__inline-note'>No search themes</span>
                  ) : (
                    <span className='evidence__modifier-list'>
                      {g.searchThemeSample.map((t, i) => (
                        <span key={`${t}-${i}`} className='evidence__audience-chip'>
                          {t}
                        </span>
                      ))}
                      {g.themesTruncated > 0 && (
                        <span className='evidence__inline-note'>+{g.themesTruncated} more</span>
                      )}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <p className='evidence__footnote'>
        Search themes raise volume and relevance and let Performance Max compete on par with
        phrase/exact keywords in shared auctions. Every active asset group should carry relevant
        themes; an empty or thin set is an underused signal.
      </p>
    </div>
  );
};
